/**
 * A yes-or-no question, asked in the game's own style.
 *
 * Not `window.confirm`: a browser dialog freezes everything behind it, cannot be
 * styled to say plainly what is at stake, and looks like an error. Used for the one
 * decision in Phase 7a that cannot be taken back — going to war.
 */

export interface ConfirmOptions {
  title: string;
  /** Plain text; it is escaped. Line breaks become paragraphs. */
  body: string;
  confirm: string;
  cancel?: string;
  /** Style the confirming button as dangerous. */
  danger?: boolean;
}

export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const root = document.createElement('div');
    root.className = 'cities-screen dialog-screen';
    const paragraphs = options.body
      .split('\n')
      .map((line) => `<p>${escapeHtml(line)}</p>`)
      .join('');
    root.innerHTML = `
      <div class="cities-panel dialog-panel" role="dialog" aria-modal="true">
        <header><h2>${escapeHtml(options.title)}</h2></header>
        <div class="dialog-body">${paragraphs}</div>
        <footer class="dialog-actions">
          <button data-answer="no">${escapeHtml(options.cancel ?? 'Cancel')}</button>
          <button data-answer="yes" class="${options.danger ? 'danger' : 'primary'}">${escapeHtml(options.confirm)}</button>
        </footer>
      </div>`;
    document.body.append(root);

    const finish = (answer: boolean): void => {
      window.removeEventListener('keydown', onKey, true);
      root.remove();
      resolve(answer);
    };
    const onKey = (event: KeyboardEvent): void => {
      // Swallow every key while the question is up, so Enter cannot also end the turn.
      event.stopPropagation();
      if (event.key === 'Escape') finish(false);
      else if (event.key === 'Enter') finish(true);
    };
    window.addEventListener('keydown', onKey, true);
    root.querySelector('[data-answer="yes"]')!.addEventListener('click', () => finish(true));
    root.querySelector('[data-answer="no"]')!.addEventListener('click', () => finish(false));
    root.addEventListener('click', (event) => {
      if (event.target === root) finish(false);
    });
    (root.querySelector('[data-answer="no"]') as HTMLButtonElement).focus();
  });
}

const escapeHtml = (text: string): string => text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
